<template>
    <v-dialog
        v-model="showDialog"
        :fullscreen="$vuetify.breakpoint.xs"
        :hide-overlay="$vuetify.breakpoint.xs"
        :transition="$vuetify.breakpoint.xs ? 'scroll-y-reverse-transition' : 'dialog-transition'"
        width="400"
    >
        <div v-if="render" class="numpad-expanded">
            <div v-if="title" class="title">
                <v-btn
                    class="mr-2"
                    icon
                    large
                    @click="showDialog = false"
                >
                    <v-icon>fal fa-times</v-icon>
                </v-btn>
                <span>{{ title }}</span>
                <v-spacer />
                <div v-if="timeout > 0" class="mr-2">{{ timeout / 1000 }}s</div>
            </div>

            <div class="content">
                <div class="wfull">
                    <template v-if="type === 'creditCard'">
                        <v-row>
                            <v-col class="flex-center" cols="3">
                                <v-btn
                                    class="switch-field-btn"
                                    color="primary"
                                    outlined
                                    fab
                                    small
                                    @click="inputReverse = !inputReverse"
                                >
                                    <v-icon>fal fa-exchange</v-icon>
                                </v-btn>
                            </v-col>

                            <v-col cols="8">
                                <v-text-field
                                    ref="textField"
                                    v-model="internalValue"
                                    class="text-field-1"
                                    :suffix="!inputReverse ? $t('general.credit_card') : $t('general.cash')"
                                    reverse
                                    readonly
                                />

                                <v-text-field
                                    :value="additionalValues.difference >= 0 ? additionalValues.difference : 0"
                                    class="text-field-2"
                                    reverse
                                    :suffix="!!inputReverse ? $t('general.credit_card') : $t('general.cash')"
                                    readonly
                                />
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col offset="3" cols="8">
                                <div v-if="!isAllegra" class="credit-card-label">
                                    <div class="text">{{ $t('general.tip') }}:</div>
                                    <div class="value">
                                        <strong>{{ (additionalValues.difference < 0 ? (-1 * additionalValues.difference) : 0).toFixed(precision) }}</strong>
                                    </div>
                                </div>
                                <div class="credit-card-label">
                                    <div class="text">{{ $t('general.price') }}:</div>
                                    <div class="value">
                                        <strong>{{ additionalValues.total }}</strong>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </template>

                    <template v-else-if="type === 'discount'">
                        <v-text-field
                            ref="textField"
                            v-model="internalValue"
                            class="text-field-1"
                            reverse
                            :suffix="$t('general.discount')"
                            prefix="%"
                            readonly
                        />

                        <div class="footer-label">
                            <div class="text">{{ $t('general.price') }}:</div>
                            <div class="value">{{ additionalValues.total }}</div>
                        </div>
                        <div class="footer-label">
                            <div class="text">{{ $t('general.discount') }}:</div>
                            <div class="value">{{ additionalValues.calculated }}</div>
                        </div>
                        <div class="footer-label">
                            <div class="text">{{ $t('general.to_pay') }}:</div>
                            <div class="value">
                                <strong>{{ additionalValues.difference }}</strong>
                            </div>
                        </div>
                    </template>

                    <template v-else-if="type === 'deposit'">
                        <v-text-field
                            ref="textField"
                            v-model="internalValue"
                            class="text-field-1"
                            reverse
                            :suffix="$t('general.deposit')"
                            readonly
                        />

                        <div class="footer-label">
                            <div class="text">{{ $t('general.deposit_current') }}:</div>
                            <div class="value">{{ additionalValues.total }}</div>
                        </div>
                        <div class="footer-label">
                            <div class="text">{{ $t('general.deposit_new') }}:</div>
                            <div class="value">
                                <strong>{{ additionalValues.calculated }}</strong>
                            </div>
                        </div>
                    </template>

                    <template v-else-if="type === 'cashRegisterDeposit'">
                        <v-text-field
                            ref="textField"
                            v-model="internalValue"
                            class="text-field-1"
                            reverse
                            :suffix="$t('general.deposit')"
                            readonly
                        />

                        <div class="footer-label">
                            <div class="text">{{ $t('general.cash_current') }}:</div>
                            <div class="value">{{ additionalValues.total }}</div>
                        </div>
                        <div class="footer-label">
                            <div class="text">{{ $t('general.cash_new') }}:</div>
                            <div class="value">
                                <strong>{{ additionalValues.calculated }}</strong>
                            </div>
                        </div>
                    </template>

                    <template v-else-if="type === 'cashRegisterCashOut'">
                        <v-text-field
                            ref="textField"
                            v-model="internalValue"
                            class="text-field-1"
                            reverse
                            :suffix="$t('general.cash_out')"
                            readonly
                        />

                        <div class="footer-label">
                            <div class="text">{{ $t('general.cash_current') }}:</div>
                            <div class="value">{{ additionalValues.total }}</div>
                        </div>
                        <div class="footer-label">
                            <div class="text">{{ $t('general.cash_new') }}:</div>
                            <div class="value">
                                <strong>{{ additionalValues.calculated }}</strong>
                            </div>
                        </div>
                    </template>

                    <template v-else-if="type === 'cash'">
                        <v-text-field
                            ref="textField"
                            v-model="internalValue"
                            class="text-field-1"
                            reverse
                            :suffix="$t('general.paid')"
                            readonly
                        />

                        <div class="footer-label highlight">
                            <div class="text">
                                <v-icon size="28" class="mx-2">fal fa-coins</v-icon>
                                <span v-if="addToDebt">{{ $t('general.change') }}:</span>
                                <span v-else>{{ $t('general.tip') }}:</span>
                            </div>
                            <div class="value">{{ additionalValues.calculated }}</div>
                        </div>

                        <div v-if="addToDebtEnabled" class="highlight-checkbox mt-5">
                            <v-checkbox
                                v-model="addToDebt"
                                class="mb-1 mt-0"
                                hide-details
                                :label="$t('general.add_to_debt')"
                            />
                        </div>
                    </template>

                    <template v-else-if="type === 'otherNoncash'">
                        <v-text-field
                            ref="textField"
                            v-model="internalValue"
                            class="text-field-1"
                            :suffix="$t('general.other_noncash_initial_cash')"
                            reverse
                            readonly
                        />

                        <div class="footer-label">
                            <div class="text">
                                {{ $t('general.other_noncash_remaining') }}:
                            </div>
                            <div class="value">{{ additionalValues.difference }}</div>
                        </div>
                    </template>

                    <!-- <template v-else-if="type === 'advance'">
                        <v-row v-if="payments.length" class="d-flex" no-gutters>
                            <v-col cols="5">
                                <v-select
                                    v-model="payments[0].type"
                                    :items="paymentTypes"
                                    item-value="value"
                                    item-text="display"
                                    :label="$t('general.paid_by')"
                                />
                            </v-col>

                            <v-col cols="6" offset="1">
                                <v-text-field
                                    ref="textField"
                                    v-model="payments[0].amount"
                                    class="text-field-1 font-size-22"
                                    :label="$t('general.amount')"
                                    reverse
                                    readonly
                                />
                            </v-col>
                        </v-row>

                        <div class="footer-label mb-4">
                            <div class="text">
                                {{ $t('general.remaining') }}:
                            </div>
                            <div class="value">{{ additionalValues.difference }}</div>
                        </div>

                        <div class="highlight-checkbox">
                            <v-checkbox
                                v-model="advance.customPaidDate"
                                class="mb-1 mt-0"
                                hide-details
                                :label="$t('general.paid_date_change')"
                                @change="advance.picker1 = advance.picker2 = false"
                            />

                            <v-slide-y-reverse-transition hide-on-leave>
                                <v-row v-if="advance.customPaidDate" class="mt-4">
                                    <v-col class="py-0" cols="8">
                                        <v-menu
                                            v-model="advance.picker1"
                                            :close-on-content-click="false"
                                            transition="scale-transition"
                                            offset-y
                                            min-width="290"
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="advance.paidDate"
                                                    :label="$t('general.paid_date')"
                                                    prepend-icon="fal fa-calendar"
                                                    readonly
                                                    v-on="on"
                                                />
                                            </template>
                                            <v-date-picker
                                                v-model="advance.paidDate"
                                                :max="moment().format('YYYY-MM-DD')"
                                                @input="advance.picker1 = false"
                                            />
                                        </v-menu>
                                    </v-col>

                                    <v-col class="py-0" cols="4">
                                        <v-menu
                                            v-model="advance.picker2"
                                            :close-on-content-click="false"
                                            transition="scale-transition"
                                            offset-y
                                            min-width="290"
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="advance.paidTime"
                                                    :label="$t('general.paid_time')"
                                                    readonly
                                                    v-on="on"
                                                />
                                            </template>
                                            <v-time-picker
                                                v-model="advance.paidTime"
                                                format="24hr"
                                                @input="advance.picker2 = false"
                                            />
                                        </v-menu>
                                    </v-col>
                                </v-row>
                            </v-slide-y-reverse-transition>
                        </div>
                    </template> -->

                    <template v-else-if="type === 'mixedPayment'">
                        <v-row
                            v-for="(payment, index) in payments"
                            :key="index"
                            class="d-flex"
                            no-gutters
                        >
                            <v-col cols="5">
                                <v-text-field
                                    v-if="index !== payments.length - 1"
                                    :value="$tc(`orders.payment_types.${payment.type}`, 2)"
                                    readonly
                                />
                                <v-select
                                    v-else
                                    v-model="payment.type"
                                    :items="paymentTypes"
                                    item-value="value"
                                    item-text="display"
                                    :label="$t('general.paid_by')"
                                />
                            </v-col>

                            <v-col cols="6" offset="1">
                                <v-text-field
                                    :ref="index === payments.length - 1 ? 'textField' : null"
                                    v-model="payment.amount"
                                    class="font-size-22"
                                    :class="{ 'text-field-1': index === payments.length - 1 }"
                                    :disabled="!payment.type"
                                    :label="$t('general.amount')"
                                    reverse
                                    readonly
                                />
                            </v-col>
                        </v-row>

                        <div class="footer-label add-payment">
                            <div class="text">
                                {{ $t('general.remaining') }}: {{ additionalValues.difference }}
                            </div>
                            <div class="d-flex">
                                <v-btn
                                    icon
                                    class="mr-2"
                                    :disabled="payments.length <= 1"
                                    @click="removePayment"
                                >
                                    <v-icon size="28">fal fa-minus-circle</v-icon>
                                </v-btn>
                                <v-btn
                                    :disabled="
                                        !parseFloat(additionalValues.difference) ||
                                        !payments[payments.length - 1].amount ||
                                        payments[payments.length - 1].amount == 0 ||
                                        paymentTypes.length <= 1
                                    "
                                    icon
                                    @click="addPayment"
                                >
                                    <v-icon size="28">fal fa-plus-circle</v-icon>
                                </v-btn>
                            </div>
                        </div>
                    </template>

                    <template v-else-if="type === 'advance'">
                        <v-row
                            v-for="(payment, index) in payments"
                            :key="index"
                            class="d-flex"
                            no-gutters
                        >
                            <v-col cols="5">
                                <v-text-field
                                    v-if="index !== payments.length - 1"
                                    :value="$tc(`orders.payment_types.${payment.type}`, 2)"
                                    readonly
                                />
                                <v-select
                                    v-else
                                    v-model="payment.type"
                                    :items="paymentTypes"
                                    item-value="value"
                                    item-text="display"
                                    :label="$t('general.paid_by')"
                                />
                            </v-col>

                            <v-col cols="6" offset="1">
                                <v-text-field
                                    :ref="index === payments.length - 1 ? 'textField' : null"
                                    v-model="payment.amount"
                                    class="font-size-22"
                                    :class="{ 'text-field-1': index === payments.length - 1 }"
                                    :disabled="!payment.type"
                                    :label="$t('general.amount')"
                                    reverse
                                    readonly
                                />
                            </v-col>
                        </v-row>

                        <div class="footer-label add-payment">
                            <div class="text">
                                {{ $t('general.remaining') }}: {{ additionalValues.difference }}
                            </div>
                            <div class="d-flex">
                                <v-btn
                                    icon
                                    class="mr-2"
                                    :disabled="payments.length <= 1"
                                    @click="removePayment"
                                >
                                    <v-icon size="28">fal fa-minus-circle</v-icon>
                                </v-btn>
                                <v-btn
                                    :disabled="
                                        !parseFloat(additionalValues.difference) ||
                                        !payments[payments.length - 1].amount ||
                                        payments[payments.length - 1].amount == 0 ||
                                        paymentTypes.length <= 1
                                    "
                                    icon
                                    @click="addPayment"
                                >
                                    <v-icon size="28">fal fa-plus-circle</v-icon>
                                </v-btn>
                            </div>
                        </div>

                        <div v-if="locale === 'sr'" class="highlight-checkbox mt-5">
                            <v-checkbox
                                v-model="advance.customPaidDate"
                                class="mb-1 mt-0"
                                hide-details
                                :label="$t('general.paid_date_change')"
                                @change="advance.picker1 = advance.picker2 = false"
                            />

                            <v-slide-y-reverse-transition hide-on-leave>
                                <v-row v-if="advance.customPaidDate" class="mt-4">
                                    <v-col class="py-0" cols="8">
                                        <v-menu
                                            v-model="advance.picker1"
                                            :close-on-content-click="false"
                                            transition="scale-transition"
                                            offset-y
                                            min-width="290"
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="advance.paidDate"
                                                    :label="$t('general.paid_date')"
                                                    prepend-icon="fal fa-calendar"
                                                    readonly
                                                    v-on="on"
                                                />
                                            </template>
                                            <v-date-picker
                                                v-model="advance.paidDate"
                                                :max="moment().format('YYYY-MM-DD')"
                                                @input="advance.picker1 = false"
                                            />
                                        </v-menu>
                                    </v-col>

                                    <v-col class="py-0" cols="4">
                                        <v-menu
                                            v-model="advance.picker2"
                                            :close-on-content-click="false"
                                            transition="scale-transition"
                                            offset-y
                                            min-width="290"
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="advance.paidTime"
                                                    :label="$t('general.paid_time')"
                                                    readonly
                                                    v-on="on"
                                                />
                                            </template>
                                            <v-time-picker
                                                v-model="advance.paidTime"
                                                format="24hr"
                                                @input="advance.picker2 = false"
                                            />
                                        </v-menu>
                                    </v-col>
                                </v-row>
                            </v-slide-y-reverse-transition>
                        </div>
                    </template>

                    <template v-else>
                        <v-text-field
                            ref="textField"
                            v-model="internalValue"
                            class="text-field-1"
                            reverse
                            readonly
                        />
                    </template>
                </div>
            </div>

            <numpad
                ref="numpad"
                v-model="internalValue"
                :precision="precision"
                :sign-button-enabled="signButtonEnabled"
                :sign-button-replace="signButtonReplace"
                :ok-button-enabled="okButtonEnabled"
                :reset-pending.sync="resetPending"
                :max="internalMax"
                :disabled="disabled"
                @confirm="confirm"
            />
        </div>
    </v-dialog>
</template>

<script>
import Numpad from '@/components/Numpad';
import CashRegister from '@/models/CashRegister'

export default {
    components: {
        Numpad
    },

    props: {
        value: {
            type: Number,
            default: 0
        },
        show: {
            type: Boolean,
            default: false
        },
        type: {
            type: String,
            default: 'simple'
        },
        title: {
            type: String,
            default: null
        },
        total: {
            type: Number,
            default: null
        },
        max: {
            type: Number,
            default: null
        },
        signButtonReplace: {
            type: String,
            default: null
        },
        persist: {
            type: Boolean,
            default: false
        },
    },

    created() {
        if (this.type === 'count') {
            this.precision = 3;
            // this.signButtonEnabled = false;
            this.signButtonEnabled = locale !== 'me';
        } else if (this.type === 'creditCard') {
            this.signButtonEnabled = false;
        } else if (this.type === 'otherNoncash') {
            this.signButtonEnabled = false;
        } else if (this.type === 'deposit') {
            this.signButtonEnabled = false;
        } else if (this.type === 'cashRegisterDeposit') {
            this.signButtonEnabled = false;
        } else if (this.type === 'userDeposit') {
            this.signButtonEnabled = false;
        } else if (this.type === 'cashRegisterCashOut') {
            this.signButtonEnabled = false;
        } else if (this.type === 'price') {
            this.precision = 3;
            this.signButtonEnabled = false;
        } else if (this.type === 'text') {
            this.precision = 3;
        }
    },

    data: () => ({
        internalValue: '0',
        internalTotal: null,
        inputReverse: true,
        showDialog: false,
        curCashRegister: null,

        precision: 2,
        signButtonEnabled: true,
        resetPending: false,

        disabled: false,
        render: false,
        timeout: 0,

        payments: [],

        advance: {
            customPaidDate: true,
            paidDate: moment().add(-1, 'days').format('YYYY-MM-DD'),
            paidTime: '12:00',
            picker1: false,
            picker2: false,
        },

        addToDebt: true,
    }),

    watch: {
        show(val) {
            this.inputReverse = true
            this.showDialog = val;

            if (val) {
                this.disabled = false;
                this.internalValue = this.value === null ? '0' : this.value.toString();
                this.internalTotal = this.total;
                this.resetPending = true;
                this.addToDebt = true;
                this.curCashRegister = CashRegister.current();

                this.selectInput();

                if(this.type === 'mixedPayment' || this.type === 'advance') {
                    this.payments = [{
                        type: this.paymentTypes[0].value,
                        amount: this.internalValue
                    }]
                }

                if(this.type === 'advance') {
                    this.advance.paidDate = moment().add(-1, 'days').format('YYYY-MM-DD');
                    this.advance.paidTime = '12:00';
                    this.advance.customPaidDate = false;
                }
            }
        },

        showDialog(val) {
            this.$emit('update:show', val);

            if(val) {
                this.render = true
            } else {
                setTimeout(() => (this.render = false), 200)
            }
        },

        value(val) {
            if(!this.persist) {
                this.internalValue = val === null ? '0' : val.toString();
            }
        },

        total(val) {
            if(!this.persist) {
                this.internalTotal = val;
            }
        },

        inputReverse(val) {
            this.internalValue = this.value === null ? '0' : this.value.toString();
        },

        internalValue(val) {
            if(!this.payments.length) return

            if(this.type === 'mixedPayment' || this.type === 'advance') {
                // calculate new total amount
                const newTotal = this.round(
                    this.payments.filter(payment => !!payment.amount)
                        .reduce((acc, cur) => acc = parseFloat(acc) + parseFloat(cur.amount), 0)
                )

                if(parseFloat(newTotal) > this.internalTotal) {
                    const curAmount = this.payments[this.payments.length - 1].amount;
                    this.internalValue = curAmount
                    return
                }

                if(this.payments[this.payments.length - 1].type) {
                    this.payments[this.payments.length - 1].amount = val;
                }
            }
        }
    },

    computed: {
        additionalValues() {
            let calculatedValue = this.round(this.internalValue);

            if(this.internalTotal !== null && this.type === 'discount') {
                calculatedValue = this.round((parseFloat(this.internalTotal) * parseFloat(this.internalValue)) / 100)
            } else if(this.internalTotal !== null && (this.type === 'deposit' || this.type === 'cashRegisterDeposit')) {
                calculatedValue = this.round((parseFloat(this.internalTotal) + parseFloat(this.internalValue)))
            } else if(this.internalTotal !== null && this.type === 'cashRegisterCashOut') {
                calculatedValue = this.round((parseFloat(this.internalTotal) - parseFloat(this.internalValue)))
            } else if(this.internalTotal !== null && this.type === 'cash') {
                calculatedValue = this.round((parseFloat(this.internalValue) - parseFloat(this.internalTotal)))
            } else if(this.internalTotal !== null && (this.type === 'mixedPayment' || this.type === 'advance')) {
                calculatedValue = this.round(
                    this.payments.filter(payment => !!payment.amount)
                        .reduce((acc, cur) => acc = parseFloat(acc) + parseFloat(cur.amount), 0)
                )
            }

            let diffValue = this.internalTotal !== null ? this.round(parseFloat(this.internalTotal) - parseFloat(calculatedValue)) : null;

            return {
                total: this.internalTotal !== null ? parseFloat(this.internalTotal).toFixed(this.precision) : null,
                calculated: calculatedValue,
                difference: diffValue
            };
        },

        okButtonEnabled() {
            if(this.type === 'cashRegisterDeposit') return this.internalValue > 0 || (locale === 'me' && this.internalValue == 0)
            if(this.type === 'cashRegisterCashOut') return this.internalValue > 0
            if(this.type === 'otherNoncash') return this.internalValue >= 0
            if(this.type === 'cash') return this.internalValue >= this.internalTotal
            if(this.type === 'mixedPayment' || this.type === 'advance') {
                return parseFloat(this.additionalValues.difference) == 0 && this.payments.every(p => !!p.type)
            }

            return true
        },

        paymentTypes() {
            if(!this.curCashRegister) return [];

            return this.curCashRegister.allowedPaymentTypes()
                .filter(type => type.value !== 'advance')
                .filter(type => {
                    return !this.payments.slice(0, this.payments.length - 1).some(payment => payment.type === type.value);
                })
        },

        internalMax() {
            if(this.type === 'mixedPayment' || this.type === 'advance') {
                const curPayments = this.payments.slice(0, this.payments.length - 1)
                    .filter(payment => !!payment.amount)
                    .reduce((acc, cur) => acc.add(Money.parse(cur.amount)), Money.parse(0));

                return Money.parse(this.internalTotal).subtract(curPayments).toRoundedUnit(2)
            } else if(this.type === 'creditCard' && !this.inputReverse && !isAllegra) {
                return null
            }

            return this.max;
        },

        noncashOnly() {
            return this.tenant && this.tenant.venue && !!this.tenant.venue.settings.noncash_only
        },

        addToDebtEnabled() {
            return locale === 'sr' && !this.curCashRegister.allowsPayment('credit_card');
        }
    },

    methods: {
        confirm() {
            if(this.disabled) return;

            this.disabled = true;

            if(this.type !== 'cash') {
                this.showDialog = false;
            } else {
                this.timeout = 3000
                const interval = setInterval(() => {
                    this.timeout -= 1000

                    if(this.timeout <= 0) {
                        this.showDialog = false
                        this.timeout = 0
                        clearInterval(interval)
                    }
                }, 1000)
            }

            this.$emit('confirm', {
                value: this.type !== 'text' ? parseFloat(this.internalValue) : this.internalValue,
                calculated: !this.inputReverse
                    ? parseFloat(this.additionalValues.difference)
                    : parseFloat(this.additionalValues.calculated),
                difference: !this.inputReverse
                    ? parseFloat(this.additionalValues.calculated)
                    : parseFloat(this.additionalValues.difference),
                total: parseFloat(this.additionalValues.total),
                payments: this.payments,
                addToDebt: this.addToDebt,
                paidDate: this.advance.customPaidDate ? `${this.advance.paidDate} ${this.advance.paidTime}:00` : null,
            });
        },

        round(val) {
            val = parseFloat(val);
            let pow = Math.pow(10, this.precision);
            let result = Math.round(val * pow) / pow;
            return result.toFixed(this.precision);
        },

        addPayment() {
            this.internalValue = this.additionalValues.difference.toString()
            this.payments.push({
                type: null,
                amount: this.additionalValues.difference.toString()
            })

            this.selectInput();
        },

        removePayment() {
            if(this.payments.length <= 1) return;

            this.payments = this.payments.slice(0, this.payments.length - 1);
            this.internalValue = this.payments[this.payments.length - 1].amount;

            this.selectInput();
        },

        selectInput() {
            if (this.internalValue === '0') return;

            setTimeout(() => {
                if(Array.isArray(this.$refs.textField)) {
                    this.$refs.textField[0]?.$refs?.input.select();
                };

                this.$refs.textField?.$refs?.input.select();

                this.resetPending = true;
            }, 200);
        },
    }
};
</script>

<style lang="scss">
.numpad-expanded {
    $textFieldColor: #2196f3;
    $textField2Color: #aeaeae;

    display: flex;
    flex-direction: column;
    height: 100%;

    .title {
        background: #d7dce8;
        padding: 10px 5px;
        display: flex;
        align-items: center;
        font-size: 20px;
    }

    .content {
        background: white;
        padding: 15px;
        min-height: 150px;
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;

        .font-size-22 {
            font-size: 22px;
        }

        .text-field-1 {
            color: $textFieldColor;
            pointer-events: none;
            font-size: 22px;

            .v-input__slot:before {
                border-color: $textFieldColor;
            }

            .v-input__slot:after {
                border: none !important;
            }

            input {
                color: $textFieldColor;
            }
        }

        .text-field-2 {
            color: $textField2Color;
            pointer-events: none;

            .v-input__slot:before {
                border-color: $textField2Color;
            }

            input {
                color: $textField2Color;
            }
        }

        .switch-field-btn {
            transform: rotate(90deg);
        }

        .credit-card-label {
            display: flex;
            justify-content: flex-end;
            font-size: 16px;

            .text {
                flex: 1;
            }

            .value {
                width: 80px;
                text-align: right;
            }
        }

        .footer-label {
            display: flex;
            font-size: 17px;
            margin: 5px 0;

            &.highlight {
                font-size: 22px;
                font-weight: 500;
            }

            .text {
                flex: 1;
            }

            .value {
                width: 80px;
                text-align: right;
            }

            &.add-payment {
                display: flex;
                justify-content: space-between;
                align-items: center;
                height: 36px;
            }
        }

        .highlight-checkbox {
            background: #f2f3f7;
            padding: 12px;
            border-radius: 3px;
        }
    }
}
</style>
